/**
 * The pricing experiments' locked arm contracts — the one source of truth for
 * what each arm charges and how its name is written wherever it is durable.
 *
 * Two experiments share this module:
 *
 * - `pricing-test-v1`, the six-leg annual price × free-trial test. ENDED: it
 *   assigns no one, but its arm keys stay decodable because they are on live
 *   Stripe subscriptions as `pricing_variant`, and a plan change is validated
 *   off that stamp.
 * - `pricing-test-v2`, the live two-arm annual price test with a 3-day trial on
 *   both arms.
 *
 * Arm keys, prices and edge codes are durable data, not display labels: they
 * are written to PostHog, Stripe subscription metadata, analytics events and
 * pinned `experiment_assignment` rows, so they must never be renamed or reused
 * after any real assignment.
 *
 * Deliberately a zero-import leaf module (like `free-plan-countries`) so the
 * IaC scripts, the API and the web client can all deep-import it.
 *
 * Full contract: docs/projects/active/pricing-experiment-ab-test-6-leg.md
 * ("Locked arm contract", D29).
 */

/** Stripe subscription-metadata key carrying the subscription's {@link PricingArmKey}. */
export const PRICING_VARIANT_METADATA_KEY = 'pricing_variant';

/** Annual price tier in whole USD. Monthly is $12.99 in every arm. */
export type PricingTestAnnualTierUsd = 39 | 59 | 79;

export interface PricingTestArm {
  annualTierUsd: PricingTestAnnualTierUsd;
  /**
   * Nominal trial days for the arm. Server-side eligibility derives the
   * effective value (an account with any retrievable prior Stripe
   * subscription gets 0), but the assigned arm — including its trial
   * dimension — is retained for intention-to-treat analysis.
   */
  trialDays: 0 | 3;
}

// =============================================================================
// pricing-test-v1 — ENDED
// =============================================================================

export const PRICING_TEST_V1_FLAG_KEY = 'pricing-test-v1';

export const PRICING_TEST_V1_VARIANTS = [
  'control',
  'price-59-no-trial',
  'price-79-no-trial',
  'price-39-trial',
  'price-59-trial',
  'price-79-trial',
] as const;

export type PricingTestV1Variant = (typeof PRICING_TEST_V1_VARIANTS)[number];

export const PRICING_TEST_V1_ARMS: Record<
  PricingTestV1Variant,
  PricingTestArm
> = {
  control: { annualTierUsd: 39, trialDays: 0 },
  'price-59-no-trial': { annualTierUsd: 59, trialDays: 0 },
  'price-79-no-trial': { annualTierUsd: 79, trialDays: 0 },
  'price-39-trial': { annualTierUsd: 39, trialDays: 3 },
  'price-59-trial': { annualTierUsd: 59, trialDays: 3 },
  'price-79-trial': { annualTierUsd: 79, trialDays: 3 },
};

/**
 * The shares v1 last apportioned, kept because the PostHog flag still
 * describes them for the ended experiment's analysis view. Nothing assigns
 * from it.
 *
 * Launched 2026-08-25 at `17/17/17/17/16/16`; the $79 arms were retired to 0
 * on 2026-09-14 (D28) with each share folded into the arm before it. The whole
 * experiment ended when `pricing-test-v2` replaced it (D29), so every v1
 * comparison is read with `scripts/reports/pricing-experiment.ts --until`.
 */
export const PRICING_TEST_V1_SPLIT: Record<PricingTestV1Variant, number> = {
  control: 0.17,
  'price-59-no-trial': 0.34,
  'price-79-no-trial': 0,
  'price-39-trial': 0.17,
  'price-59-trial': 0.32,
  'price-79-trial': 0,
};

/** True when the v1 arm offers a trial (nominal — before eligibility). */
export const pricingVariantHasTrial = (
  variant: PricingTestV1Variant,
): boolean => PRICING_TEST_V1_ARMS[variant].trialDays > 0;

// =============================================================================
// pricing-test-v2 — live
// =============================================================================

/**
 * The live experiment's key, in all three places it names the same thing: the
 * `EdgeBucketing.pricingTestV2` definition the edge/lambda/API hash against,
 * the `experiment_assignment.experiment_key` the account pin is stored under,
 * and the PostHog flag the analysis reads.
 *
 * **Assignment is ours, not PostHog's.** The arm is computed from the
 * `lis_bucket` cookie (or, with no cookie, the account id) and frozen onto the
 * account at signup or at its first paywall read, whichever comes first.
 * PostHog never answers "what variant" for this experiment; its flag exists so
 * the experiment view has a vocabulary to analyze `$feature/pricing-test-v2`
 * against.
 */
export const PRICING_TEST_V2_FLAG_KEY = 'pricing-test-v2';

/** The PostHog variants. PostHog requires an experiment to have `control`. */
export const PRICING_TEST_V2_VARIANTS = ['control', 'price-59-trial'] as const;

export type PricingTestV2Variant = (typeof PRICING_TEST_V2_VARIANTS)[number];

export const PRICING_TEST_V2_SPLIT: Record<PricingTestV2Variant, number> = {
  control: 0.5,
  'price-59-trial': 0.5,
};

/**
 * The arm key each v2 variant is stamped into Stripe and sent over the API
 * as. Qualified with the flag key because v1's `control` is already on live
 * subscriptions meaning $39 with no trial, and a stamp has to decode to one
 * offer forever.
 */
export const PRICING_TEST_V2_ARM_KEYS = {
  control: 'pricing-test-v2:control',
  'price-59-trial': 'pricing-test-v2:price-59-trial',
} as const satisfies Record<PricingTestV2Variant, string>;

export type PricingTestV2ArmKey =
  (typeof PRICING_TEST_V2_ARM_KEYS)[PricingTestV2Variant];

/**
 * The single-character code each v2 variant is hashed and stamped as at the
 * edge, where the viewer-request bundle has no room for arm keys (see
 * docs/projects/active/edge-experiments-account-pinned-bucketing.md §7). It
 * appears in the `split` keys, the `x-variants` header and the pinned
 * `experiment_assignment.variant` rows; everything else carries the variant or
 * the arm key. Frozen like the keys: re-lettering re-rolls every visitor and
 * strands every pin.
 */
export const PRICING_TEST_V2_EDGE_CODES = {
  control: 'a',
  'price-59-trial': 'b',
} as const satisfies Record<PricingTestV2Variant, string>;

export type PricingTestV2EdgeCode =
  (typeof PRICING_TEST_V2_EDGE_CODES)[PricingTestV2Variant];

/**
 * Decode an edge code read back from `x-variants`, a pinned row or a DevTools
 * override into the variant PostHog knows. Null for anything else.
 */
export const decodePricingEdgeCode = (value: unknown) =>
  PRICING_TEST_V2_VARIANTS.find(
    (variant) => PRICING_TEST_V2_EDGE_CODES[variant] === value,
  ) ?? null;

// =============================================================================
// Arm keys — both experiments' durable vocabulary
// =============================================================================

/**
 * Every arm any pricing experiment has ever assigned, keyed the way Stripe's
 * `pricing_variant` stamp and the API wire write it.
 */
export type PricingArmKey = PricingTestV1Variant | PricingTestV2ArmKey;

export const PRICING_ARMS: Record<PricingArmKey, PricingTestArm> = {
  ...PRICING_TEST_V1_ARMS,
  [PRICING_TEST_V2_ARM_KEYS.control]: { annualTierUsd: 39, trialDays: 3 },
  [PRICING_TEST_V2_ARM_KEYS['price-59-trial']]: {
    annualTierUsd: 59,
    trialDays: 3,
  },
};

/**
 * Decode a `pricing_variant` read back from Stripe metadata or an API
 * response. Returns null for anything that is not a known arm key. Callers
 * decide the policy: null on a subscription means "not an experiment
 * subscription"; an unknown non-null stamp on a trialing subscription is an
 * invariant violation and fails closed.
 */
export const decodePricingArm = (value: unknown) =>
  typeof value === 'string' && Object.hasOwn(PRICING_ARMS, value)
    ? (value as PricingArmKey)
    : null;

/** The v2 arm key for an edge code, or null when it is not a live code. */
export const pricingArmForEdgeCode = (value: unknown) => {
  const variant = decodePricingEdgeCode(value);
  return variant === null ? null : PRICING_TEST_V2_ARM_KEYS[variant];
};

/** The PostHog variant a v2 arm key reports as; null for a v1 arm. */
export const pricingTestV2VariantForArm = (arm: PricingArmKey) =>
  PRICING_TEST_V2_VARIANTS.find(
    (variant) => PRICING_TEST_V2_ARM_KEYS[variant] === arm,
  ) ?? null;

/**
 * C4 as an enforceable rule: every viewer the edge cannot bucket — crawlers,
 * cookie-less first requests — is served `fallbackVariant` with no exposure
 * event, so if that arm were cheaper than an arm checkout can charge, a
 * search engine could index a price the checkout will not honour (Mode C
 * design §3.5, C4). Only arms the split assigns count, and the fallback must
 * be one of them.
 *
 * Accepts both vocabularies — edge codes and PostHog variants — so it can
 * judge the edge definition and test fixtures alike. Returns null when the
 * rule holds, or the sentence to fail with. The live edge definition asserts
 * this at module load (`client-flags.ts`), so a bad fallback is a refused
 * definition, not a test failure someone has to run.
 */
export const pricingFallbackViolation = (def: {
  readonly key: string;
  readonly variants: readonly string[];
  readonly split: Readonly<Record<string, number>>;
  readonly fallbackVariant: string;
}): string | null => {
  const decode = (value: string) =>
    decodePricingEdgeCode(value) ??
    PRICING_TEST_V2_VARIANTS.find((variant) => variant === value) ??
    null;
  const annualUsd = (variant: PricingTestV2Variant) =>
    PRICING_ARMS[PRICING_TEST_V2_ARM_KEYS[variant]].annualTierUsd;
  const isLive = (name: string) => (def.split[name] ?? 0) > 0;

  const fallback = decode(def.fallbackVariant);
  if (fallback === null) {
    return `${def.key} declares fallbackVariant "${def.fallbackVariant}", which is not a ${PRICING_TEST_V2_FLAG_KEY} arm, so no price can be implied for the viewers who receive it.`;
  }
  if (!isLive(def.fallbackVariant)) {
    return `${def.key} declares fallbackVariant "${def.fallbackVariant}", which the split does not assign; crawlers and cookie-less viewers would be advertised a price no visitor can be quoted.`;
  }
  let topArm = fallback;
  for (const name of def.variants) {
    const arm = decode(name);
    if (arm === null) {
      return `${def.key} lists variant "${name}", which is not a ${PRICING_TEST_V2_FLAG_KEY} arm; a price experiment's arms must all be priceable.`;
    }
    if (isLive(name) && annualUsd(arm) > annualUsd(topArm)) topArm = arm;
  }
  if (annualUsd(fallback) >= annualUsd(topArm)) return null;
  return `${def.key} declares fallbackVariant "${fallback}" ($${annualUsd(fallback)}/yr) while arm "${topArm}" charges $${annualUsd(topArm)}/yr. Crawlers and cookie-less viewers are served the fallback, so this advertises $${annualUsd(fallback)} for a page whose checkout can charge $${annualUsd(topArm)} — the advertised-below-charged direction design C4 exists to make impossible.`;
};
